// utils/googleMapsLoader.js
import { Loader } from '@googlemaps/js-api-loader';
import { getActiveLanguage } from '../i18n/languageStore.js';
import { resolveLanguage } from '../i18n/localeManifest.js';

export const shouldReloadForGoogleMapsLanguageChange = ({
  previousLanguage,
  nextLanguage,
  sdkLanguage = null,
  sdkLanguageBound = false,
}) => {
  const previous = resolveLanguage(previousLanguage);
  const next = resolveLanguage(nextLanguage);
  if (!sdkLanguageBound || previous === next) return false;

  // For an SDK loaded outside this singleton the exact language is unknown;
  // the currently active application language is the safest baseline.
  return resolveLanguage(sdkLanguage || previous) !== next;
};

export class GoogleMapsLoader {
  constructor() {
    this.loader = null;
    this.isLoaded = false;
    this.isLoading = false;
    this.loadPromise = null;
    this.loaderLanguage = null;
  }

  async load() {
    // Dacă se încarcă deja, returnează promisiunea existentă
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    // `window.google.maps` apare ca bootstrap stub înainte ca librăriile cerute
    // să fie gata. Considerăm încărcarea completă doar când Places există.
    if (typeof window !== 'undefined' && window.google?.maps?.places) {
      this.isLoaded = true;
      return Promise.resolve();
    }

    // Dacă nu există loader-ul, îl creăm
    if (!this.loader) {
      const apiKey = String(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();
      if (!apiKey) {
        throw new Error('REACT_APP_GOOGLE_MAPS_API_KEY lipsește din configurația buildului');
      }

      this.loaderLanguage = resolveLanguage(getActiveLanguage());
      this.loader = new Loader({
        apiKey,
        version: "weekly",
        language: this.loaderLanguage,
        libraries: ["places", "geometry", "visualization"] // Toate bibliotecile necesare
      });
    }

    this.isLoading = true;
    this.loadPromise = this.loader.load()
      .then(() => {
        this.isLoaded = true;
        this.isLoading = false;
        return Promise.resolve();
      })
      .catch((error) => {
        this.isLoading = false;
        this.loadPromise = null;
        throw error;
      });

    return this.loadPromise;
  }

  isGoogleMapsLoaded() {
    return typeof window !== 'undefined' && Boolean(window.google?.maps?.places);
  }

  isSdkLanguageBound() {
    // @googlemaps/js-api-loader keeps a process-wide singleton and rejects a
    // second Loader constructed with different options. Once a Loader exists,
    // even a failed attempt is therefore bound to its original language until
    // the document is reloaded.
    return Boolean(this.loader)
      || this.isLoading
      || this.isLoaded
      || this.isGoogleMapsLoaded()
      || (typeof window !== 'undefined' && Boolean(window.google?.maps));
  }

  requiresPageReload(previousLanguage, nextLanguage) {
    return shouldReloadForGoogleMapsLanguageChange({
      previousLanguage,
      nextLanguage,
      sdkLanguage: this.loaderLanguage,
      sdkLanguageBound: this.isSdkLanguageBound(),
    });
  }
}

// Exportăm o singură instanță (singleton)
export const googleMapsLoader = new GoogleMapsLoader();

export const reloadPageForGoogleMapsLanguageChange = ({
  previousLanguage,
  nextLanguage,
  mapsLoader = googleMapsLoader,
  reload,
}) => {
  if (!mapsLoader.requiresPageReload(previousLanguage, nextLanguage)) return false;

  if (!reload && typeof window === 'undefined') return false;
  const reloadPage = reload || (() => window.location.reload());
  try {
    reloadPage();
    return true;
  } catch (error) {
    // The preference is already committed at this point. Keep it committed and
    // report the navigation failure instead of rolling the language back.
    console.error('Could not reload the page after changing Google Maps language:', error);
    return false;
  }
};
